import { Flatfile } from "@flatfile/api";
import { WSQueryConfig } from "@ws-react-query";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import { BATCH_LABELS_WS_KEYS } from "@wingspanhq/payments/dist/lib/constants";
import { useWSQuery } from "../../../../query/helpers";
import {
  FLATFILE_REJECTED_VALID_ROWS_FILTER,
  FLATFILE_UPLOAD_JOB_REFETCH_INTERVAL
} from "../../constants";
import { useFlatfileApiClient } from "../../hooks/useFlatfileApiClient";
import { BulkResource } from "../../types";
import { isUploadInProgress } from "../../utils/bulkBatchUtils";
import { getFlatfileMainSheetId } from "../../utils/getFlatfileMainSheetId";
import {
  QUERY_FLATFILE_SKIPPED_ROWS_COUNT,
  QUERY_FLATFILE_UPLOAD_JOB
} from "./keys";

const FINISHED_UPLOAD_JOB_STATUSES: Flatfile.JobStatus[] = [
  Flatfile.JobStatus.Complete,
  Flatfile.JobStatus.Failed,
  Flatfile.JobStatus.Canceled
];

const isUploadJobFinished = (uploadJob?: Flatfile.Job): boolean =>
  !!uploadJob?.status &&
  FINISHED_UPLOAD_JOB_STATUSES.includes(uploadJob.status);

/**
 * Flatfile job that uploads the spreadsheet rows into the bulk batch (the parent "main action"
 * job). It is the only source of upload progress and of upload failures: when the job dies, the
 * batch keeps its `flatfileJobId` label and never receives `uploadFinishedAt`.
 */
export const useQueryFlatfileUploadJob = (
  bulkBatch?: IBulkBatch,
  config?: WSQueryConfig<Flatfile.Job, Error>
) => {
  const flatfileApiClient = useFlatfileApiClient(bulkBatch);
  const jobId = bulkBatch?.labels?.[BATCH_LABELS_WS_KEYS.flatfileJobId] as
    | string
    | undefined;

  return useWSQuery<Flatfile.Job, Error>(
    [QUERY_FLATFILE_UPLOAD_JOB, jobId],
    async () => {
      const { data: job } = await flatfileApiClient!.jobs.get(jobId!);
      return job;
    },
    {
      // the job is only interesting while the upload runs; afterwards the batch labels tell the
      // whole story, so finished batches must not hit Flatfile on every detail page view
      enabled: !!flatfileApiClient && !!jobId && isUploadInProgress(bulkBatch),
      // keep polling while the upload is running, the job is the only thing that moves
      refetchInterval: uploadJob =>
        isUploadInProgress(bulkBatch) && !isUploadJobFinished(uploadJob)
          ? FLATFILE_UPLOAD_JOB_REFETCH_INTERVAL
          : false,
      retry: false,
      ...config
    }
  );
};

/**
 * Number of uploaded spreadsheet rows that did not make it into the batch.
 *
 * Counted in Flatfile rather than read off the `flatfileRejectedRowsCount` batch label, because the
 * label only counts rows the Wingspan API refused. Rows that failed validation are never submitted
 * at all - the uploader fetches with `filter: "valid"` - so they are missing from the label even
 * though they are exactly the rows the customer sees as skipped. Error state covers both.
 *
 * Rows the uploader stamped Rejected that Flatfile still marks valid are counted separately, kept to
 * valid rows so nothing is counted twice. The skipped-rows CSV downloads the same two sets, so the
 * number and the file can never disagree.
 *
 * Callers should still fall back to the label when this returns undefined: the count needs a live
 * Flatfile space, and the download is equally unavailable once that space is gone.
 */
export const useQueryFlatfileSkippedRowsCount = (
  bulkBatch?: IBulkBatch,
  bulkResource?: BulkResource,
  config?: WSQueryConfig<number, Error>
) => {
  const flatfileApiClient = useFlatfileApiClient(bulkBatch);
  const spaceId = bulkBatch?.labels?.[BATCH_LABELS_WS_KEYS.flatfileBatchId] as
    | string
    | undefined;

  return useWSQuery<number, Error>(
    [QUERY_FLATFILE_SKIPPED_ROWS_COUNT, spaceId],
    async () => {
      const sheetId = await getFlatfileMainSheetId(
        flatfileApiClient!,
        bulkBatch!,
        bulkResource!
      );
      const [{ data: errorCounts }, { data: rejectedValidCounts }] =
        await Promise.all([
          flatfileApiClient!.sheets.getRecordCounts(sheetId),
          flatfileApiClient!.sheets.getRecordCounts(
            sheetId,
            FLATFILE_REJECTED_VALID_ROWS_FILTER
          )
        ]);

      return errorCounts.counts.error + rejectedValidCounts.counts.total;
    },
    {
      // while the upload is still running the number is meaningless - parts are still submitting
      // rows and enriching rejections - so this only runs once the upload has come to rest
      enabled:
        !!flatfileApiClient &&
        !!spaceId &&
        !!bulkResource &&
        !isUploadInProgress(bulkBatch),
      retry: false,
      ...config
    }
  );
};
